/**
 * `<ValidationSummarySection/>` — lead-side validation summary for the persistent right panel (PR 3, #198).
 *
 * Renders a one-line status (✓ All H pass / N issues / N to review) for
 * the current cycle's validation state with a "View details →" deep
 * link to the Roster tab where the full `<ValidationPanel/>` lives.
 *
 * Always-on per #193 (validation is core scheduling, not opt-in).
 *
 * **Mount conditions.**
 *   - Cycle context resolved from URL (`useParams.cycleId`).
 *   - Cycle is in `published` state — pre-publish there is no roster
 *     to validate; the section silently hides rather than rendering a
 *     confusing empty card.
 *
 * **Data flow.** Mirrors the existing `<ValidationPanel/>` flow:
 *   1. GET `/api/cycles/{id}/roster` — pull the assignment-set.
 *   2. POST `/api/cycles/{id}/validate-roster` — re-check H1-H15.
 *
 * Cached under `["validate-roster", cycleId, accountId]` so a panel
 * mount + a `<ValidationPanel/>` mount on the same cycle don't both
 * fire the (heavier) POST. Reuses the shared `cycleRosterQueryKey`
 * for the GET so the cache hit with `<RosterHeatmap/>` /
 * `<CoverageGapsSection/>` is free.
 *
 * **Self-hides** on fetch error or when the cycle isn't published.
 *
 * **Focal-component discipline** per `feedback_ui_section_focal_component.md`:
 * the badge (count + tone) is the section focal; the header + deep-link
 * are calm supporting context.
 *
 * **Calmer-noun discipline** per Round E + Tom UX log: never "violation"
 * / "breach" in user-visible copy. "Issues to review" is the surface
 * noun; the API field name (`violations`) stays unchanged underneath.
 */
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { type ApiError, apiFetch } from "../api/client";
import type { components } from "../api/schema.gen";
import { useCycle } from "../api/useCycle";
import { cycleRosterQueryKey } from "../api/useCycleRoster";
import { useAuth } from "../lib/AuthContext";
import { AmbientErrorNotice, isUnexpectedApiError } from "./AmbientErrorNotice";
import { AmbientPill } from "./AmbientPill";
import { Badge } from "./ui";

type RosterResponse = components["schemas"]["RosterResponse"];
type ValidationResponse = components["schemas"]["ValidateRosterResponse"];

/**
 * The section's reads + derived count, shared with the panel handle's
 * badge (#1920, `lib/persistentPanelBadge.ts`) so both read the one
 * `["validate-roster", …]` cache entry. `showsCount` is true exactly when
 * the section renders its "N issues to review" line.
 */
export function useValidationSummary(cycleId: string | undefined) {
  const { accountId } = useAuth();
  const cycleQuery = useCycle(cycleId);

  const cycleState = cycleQuery.data?.state;
  const isPublished = cycleState === "published";
  const enabled = Boolean(cycleId && accountId && isPublished);

  // Two-step fetch — GET roster, then POST validate. Composed inside
  // the queryFn so TanStack treats it as a single cacheable unit.
  const validationQuery = useQuery<ValidationResponse, ApiError | Error>({
    queryKey: ["validate-roster", cycleId ?? "", accountId ?? ""],
    enabled,
    staleTime: 30_000,
    retry: false,
    queryFn: async () => {
      const roster = await apiFetch<RosterResponse>(`/api/cycles/${cycleId}/roster`);
      const candidates = roster.assignments.map((a) => ({
        membership_id: a.member_id,
        date: a.date,
        role: a.role,
      }));
      return apiFetch<ValidationResponse>(`/api/cycles/${cycleId}/validate-roster`, {
        method: "POST",
        body: JSON.stringify({ assignments: candidates }),
      });
    },
  });

  // Pre-warm the shared roster cache key so a subsequent navigation
  // to the Roster tab gets the GET payload from cache. The QueryClient
  // already handles this automatically via the apiFetch — but we read
  // the key here to keep the `cycleRosterQueryKey` import wired in case
  // a future invalidation needs it.
  void cycleRosterQueryKey(cycleId ?? "", accountId);

  const issueCount = useMemo(
    () => validationQuery.data?.violations?.length ?? 0,
    [validationQuery.data],
  );

  const showsCount =
    Boolean(cycleId) &&
    isPublished &&
    !cycleQuery.isError &&
    !validationQuery.isError &&
    !validationQuery.isLoading &&
    issueCount > 0;

  return { cycleQuery, cycleState, isPublished, validationQuery, issueCount, showsCount };
}

export function ValidationSummarySection() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const { cycleQuery, cycleState, isPublished, validationQuery, issueCount } =
    useValidationSummary(cycleId);

  if (!cycleId) return null;
  // An unexpected cycle-fetch error surfaces a calm notice; a 404
  // (cycle absent) keeps hiding.
  if (cycleQuery.isError) {
    if (isUnexpectedApiError(cycleQuery.error)) {
      return (
        <AmbientErrorNotice
          onRetry={() => void cycleQuery.refetch()}
          label="Validation"
          testId="validation-summary-section-error"
        />
      );
    }
    return null;
  }
  if (cycleQuery.isLoading || !cycleState) return null;
  // Pre-publish there's nothing to validate — stay hidden BEFORE
  // considering the validation fetch (which only fires when published).
  if (!isPublished) return null;
  if (validationQuery.isError) {
    if (isUnexpectedApiError(validationQuery.error)) {
      return (
        <AmbientErrorNotice
          onRetry={() => void validationQuery.refetch()}
          label="Validation"
          testId="validation-summary-section-error"
        />
      );
    }
    return null;
  }

  return (
    <AmbientPill
      variant="flat"
      title="Validation"
      ariaLabel="Validation summary"
      detailTo={`/cycles/${cycleId}/roster`}
      detailLabel="View details"
      detailArrow
      detailLinkClassName="flex items-center gap-1 text-xs text-fg-muted hover:text-fg"
      detailTestId="validation-summary-deep-link"
      testId="validation-summary-section"
      isLoading={validationQuery.isLoading}
      skeletonClassName="h-8"
      skeletonTestId="validation-summary-loading"
    >
      {issueCount === 0 ? (
        <div className="flex items-center gap-2" data-testid="validation-summary-clear">
          <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
          <span className="text-sm font-medium text-fg">All H pass</span>
          <Badge tone="neutral">0 issues</Badge>
        </div>
      ) : (
        <div
          className="flex items-center gap-2"
          data-testid="validation-summary-issues"
          data-issue-count={issueCount}
        >
          <AlertTriangle className="h-4 w-4 text-warning" aria-hidden="true" />
          <span className="text-sm font-medium text-fg">
            {issueCount} {issueCount === 1 ? "issue" : "issues"} to review
          </span>
        </div>
      )}
    </AmbientPill>
  );
}
